import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowUpDownIcon,
  ChevronDownIcon,
  DownloadIcon,
  LoaderIcon,
  PlusIcon,
  RotateCcwIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import z from "zod";

import { ClearableSearchInput, Column, Row } from "@/components";
import { SearchableLocationFilter } from "./SearchableLocationFilter";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useDownloadCompanyReport } from "@/hooks/mutations/useDownloadCompanyReport";
import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import { defaultJobsSortKeyAndOrder, jobStatusOptions } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { JobsSortKeyAndOrder, JobStatus } from "@/types";

const statusFilterSchema = z.object({
  statuses: z.array(z.string()),
});

const StatusFilterDropdownForm = ({
  isInDefaultState,
  setStatuses,
  statuses,
}: {
  isInDefaultState: boolean;
  setStatuses: (statuses: JobStatus[]) => void;
  statuses: JobStatus[];
}) => {
  const [open, setOpen] = useState(false);

  const form = useForm<z.infer<typeof statusFilterSchema>>({
    defaultValues: { statuses },
    resolver: zodResolver(statusFilterSchema),
  });

  const handleOpenChange = (open: boolean) => {
    setOpen(open);

    // If we are closing the dropdown here it means we haven't applied
    // the filter, so reset the form to the current statuses.
    //
    // We also delay the reset slightly to avoid the reset happening
    // while the dropdown is closing.
    if (!open) {
      setTimeout(() => {
        form.reset({ statuses });
      }, 150);
    }
  };

  const handleSubmit = (data: z.infer<typeof statusFilterSchema>) => {
    setStatuses(data.statuses as JobStatus[]);
    setOpen(false);
  };

  // Need to reset the form when statuses change as a result of global reset.
  useEffect(() => {
    form.reset({ statuses });
  }, [form, statuses]);

  return (
    <DropdownMenu onOpenChange={handleOpenChange} open={open}>
      <DropdownMenuTrigger asChild>
        <Button
          className="rounded-full"
          variant={isInDefaultState ? "outlineSecondary" : "filterOrSortActive"}
        >
          <LoaderIcon className="h-4 w-4" />
          Status
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px] p-0">
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="statuses"
              render={() => (
                <FormItem className="p-4">
                  <FormLabel>Status</FormLabel>
                  {jobStatusOptions.map((jobStatusOption) => (
                    <FormField
                      key={jobStatusOption.value}
                      control={form.control}
                      name="statuses"
                      render={({ field }) => {
                        return (
                          <FormItem className="flex gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value.includes(
                                  jobStatusOption.value,
                                )}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    field.onChange([
                                      ...field.value,
                                      jobStatusOption.value,
                                    ]);
                                  } else {
                                    field.onChange(
                                      field.value.filter(
                                        (value) =>
                                          (value as JobStatus) !==
                                          jobStatusOption.value,
                                      ),
                                    );
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {jobStatusOption.label}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </FormItem>
              )}
            />
            <Row className="justify-end border-t border-slate-200 p-2">
              <Button variant="ghost" type="submit">
                Apply
              </Button>
            </Row>
          </form>
        </Form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const sortBySchema = z.object({
  sortKeyAndOrder: z.nativeEnum(JobsSortKeyAndOrder, {
    required_error: "Required",
  }),
});

const SortByDropdownForm = ({
  isInDefaultState,
  setSortOrderAndKey,
  sortKeyAndOrder,
}: {
  isInDefaultState: boolean;
  setSortOrderAndKey: (sortOrderAndKey: JobsSortKeyAndOrder) => void;
  sortKeyAndOrder: JobsSortKeyAndOrder;
}) => {
  const [open, setOpen] = useState(false);

  const form = useForm<z.infer<typeof sortBySchema>>({
    defaultValues: {
      sortKeyAndOrder,
    },
    resolver: zodResolver(sortBySchema),
  });

  const handleOpenChange = (open: boolean) => {
    setOpen(open);

    // If we are closing the dropdown here it means we haven't
    // applied the sort, so reset the form to the current sort.
    if (!open) {
      form.reset({ sortKeyAndOrder });
    }
  };

  const handleSubmit = (data: z.infer<typeof sortBySchema>) => {
    setSortOrderAndKey(data.sortKeyAndOrder);
    setOpen(false);
  };

  // Need to reset the form when the sort changes as a result of global reset.
  useEffect(() => {
    form.reset({ sortKeyAndOrder });
  }, [form, sortKeyAndOrder]);

  return (
    <DropdownMenu onOpenChange={handleOpenChange} open={open}>
      <DropdownMenuTrigger asChild>
        <Button
          className="rounded-full"
          variant={isInDefaultState ? "outlineSecondary" : "filterOrSortActive"}
        >
          <ArrowUpDownIcon className="h-4 w-4" />
          <span>
            <span className="font-semibold">Sort by:</span> {sortKeyAndOrder}
          </span>
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px] p-0">
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="sortKeyAndOrder"
              render={({ field }) => (
                <FormItem className="p-4 text-slate-700">
                  <FormLabel>Sort by</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      {Object.entries(JobsSortKeyAndOrder).map(
                        ([key, value]) => (
                          <FormItem key={key} className="flex gap-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={value} />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {value}
                            </FormLabel>
                          </FormItem>
                        ),
                      )}
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            <Row className="justify-end border-t border-slate-200 p-2">
              <Button variant="ghost" type="submit">
                Apply
              </Button>
            </Row>
          </form>
        </Form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const JobsFilters = ({
  allCities,
  cities,
  setCities,
  setSortOrderAndKey,
  setStatuses,
  setTitle,
  sortKeyAndOrder,
  statuses,
  title,
}: {
  allCities: string[];
  cities: string[];
  setCities: (cities: string[]) => void;
  setSortOrderAndKey: (sortOrderAndKey: JobsSortKeyAndOrder) => void;
  setStatuses: (statuses: JobStatus[]) => void;
  setTitle: (title: string) => void;
  sortKeyAndOrder: JobsSortKeyAndOrder;
  statuses: JobStatus[];
  title: string;
}) => {
  /* Database CRUD Operations */
  // Read
  const currentCompanyId = useCurrentCompanyId();
  const companies = useGetCachedCompaniesSummary();
  // Update
  const { mutate: downloadCompanyReport, isPending: isDownloadingReport } =
    useDownloadCompanyReport();

  const currentCompany = companies.find(
    (company) => company.id === currentCompanyId,
  );

  const isSearchInDefaultState = title === "";
  const isSortInDefaultState = defaultJobsSortKeyAndOrder === sortKeyAndOrder;
  const isCityFilterInDefaultState = cities.length === 0;
  const isStatusFilterInDefaultState = statuses.length === 0;

  const shouldShowResetButton =
    !isSearchInDefaultState ||
    !isSortInDefaultState ||
    !isCityFilterInDefaultState ||
    !isStatusFilterInDefaultState;

  const handleReset = () => {
    setTitle("");
    setSortOrderAndKey(defaultJobsSortKeyAndOrder);
    setCities([]);
    setStatuses([]);
  };

  const handleDownloadReport = () => {
    mixpanelUtils.trackButtonClick("Download report", MIXPANEL_SCREENS.COMPANY);
    downloadCompanyReport({
      companyId: currentCompanyId,
      companyName: currentCompany?.name ?? "Company",
      window: "month",
    });
  };

  return (
    <Column className="items-start gap-4">
      {/* https://github.com/shadcn-ui/ui/issues/1562 */}
      <ClearableSearchInput
        containerClassName="w-[338px]"
        onChange={setTitle}
        placeholder="Search by job title"
        value={title}
      />
      <Row className="w-full items-center justify-between">
        <Row className="items-center gap-2">
          <SortByDropdownForm
            isInDefaultState={isSortInDefaultState}
            setSortOrderAndKey={setSortOrderAndKey}
            sortKeyAndOrder={sortKeyAndOrder}
          />
          <SearchableLocationFilter
            allLocations={allCities}
            isInDefaultState={isCityFilterInDefaultState}
            locations={cities}
            setLocations={setCities}
          />
          <StatusFilterDropdownForm
            isInDefaultState={isStatusFilterInDefaultState}
            setStatuses={setStatuses}
            statuses={statuses}
          />
          {shouldShowResetButton && (
            <Button
              className="rounded-full text-slate-500 hover:text-slate-500"
              onClick={handleReset}
              variant="ghost"
            >
              <RotateCcwIcon className="h-4 w-4" />
              Reset
            </Button>
          )}
        </Row>
        <Row className="items-center gap-2">
          <Button
            className="rounded-full"
            disabled={isDownloadingReport}
            onClick={handleDownloadReport}
            variant="outlineSecondary"
          >
            <DownloadIcon className="h-4 w-4" />
            Download Report
          </Button>
          <Button
            className="rounded-full"
            asChild
            onClick={() => {
              mixpanelUtils.trackButtonClick(
                "Create job",
                MIXPANEL_SCREENS.COMPANY,
              );
            }}
          >
            {/* TODO: Way to ensure that this always goes to the right route. */}
            <Link to="jobs/create">
              <PlusIcon className="h-4 w-4" />
              Create job
            </Link>
          </Button>
        </Row>
      </Row>
    </Column>
  );
};
